/**
 * Расстановка флота на клиенте.
 *
 * Дублирует логику серверного `engine-seabattle`, но без `node:crypto` —
 * браузерная сборка не должна тянуть серверный движок. Это единственное
 * намеренное дублирование в проекте, и оно безопасно: сервер всё равно
 * проверяет расстановку сам, а здешний код нужен только чтобы показать
 * человеку корректный вариант до отправки.
 */

export const SIZE = 10;
export const CELLS = SIZE * SIZE;
export const FLEET_SPEC = [4, 3, 3, 2, 2, 2, 1, 1, 1, 1] as const;

const xOf = (i: number) => i % SIZE;
const yOf = (i: number) => Math.floor(i / SIZE);

export function neighbours(cell: number): number[] {
  const x = xOf(cell);
  const y = yOf(cell);
  const out: number[] = [];
  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      if (dx === 0 && dy === 0) continue;
      const nx = x + dx;
      const ny = y + dy;
      if (nx >= 0 && nx < SIZE && ny >= 0 && ny < SIZE) out.push(ny * SIZE + nx);
    }
  }
  return out;
}

/** Быстрая проверка перед отправкой — окончательное слово всё равно за сервером. */
export function validateFleet(fleet: number[][]): boolean {
  if (fleet.length !== FLEET_SPEC.length) return false;
  const occupied = new Set<number>();

  for (const cells of fleet) {
    if (cells.length === 0) return false;
    for (const c of cells) {
      if (!Number.isInteger(c) || c < 0 || c >= CELLS) return false;
      if (occupied.has(c)) return false;
      for (const n of neighbours(c)) if (occupied.has(n)) return false;
    }
    for (const c of cells) occupied.add(c);
  }

  const got = fleet.map((s) => s.length).sort((a, b) => b - a);
  const want = [...FLEET_SPEC].sort((a, b) => b - a);
  return got.every((v, i) => v === want[i]);
}

/** Случайная корректная расстановка. */
export function randomFleet(): number[][] {
  for (let attempt = 0; attempt < 200; attempt++) {
    const occupied = new Set<number>();
    const blocked = new Set<number>();
    const fleet: number[][] = [];
    let ok = true;

    // Крупные корабли первыми: под конец свободного места мало.
    for (const size of FLEET_SPEC) {
      const placed = tryPlace(size, occupied, blocked);
      if (!placed) {
        ok = false;
        break;
      }
      fleet.push(placed);
    }
    if (ok) return fleet;
  }
  // Практически недостижимо, но лучше вернуть хоть что-то, чем упасть.
  throw new Error("не удалось расставить флот");
}

function tryPlace(size: number, occupied: Set<number>, blocked: Set<number>): number[] | null {
  for (let attempt = 0; attempt < 400; attempt++) {
    const horizontal = Math.random() < 0.5;
    const x = Math.floor(Math.random() * (horizontal ? SIZE - size + 1 : SIZE));
    const y = Math.floor(Math.random() * (horizontal ? SIZE : SIZE - size + 1));

    const cells: number[] = [];
    for (let i = 0; i < size; i++) {
      cells.push(horizontal ? y * SIZE + x + i : (y + i) * SIZE + x);
    }
    if (cells.some((c) => occupied.has(c) || blocked.has(c))) continue;

    for (const c of cells) {
      occupied.add(c);
      for (const n of neighbours(c)) blocked.add(n);
    }
    return cells;
  }
  return null;
}
